import React, { useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import FooterSummary from './FooterSummary'
import Table from '../../../../../../components/Table'

import { ContractBuilderContext } from '../ContractBuilderContext'
import { MediaOrderFormContext } from '../../../MediaOrderFormContext'

import useSaveData from './useSaveData'
import useReachData from './useReachData'
import useOrderProducts from './useOrderProducts'
import { useAssetColumn } from './cols/AssetCol/useAssetColumn'
import usePreviewProductColumns from './cols/usePreviewProductColumns'
import { useDisableProductsActions } from './useDisableProductsActions'
import { useFetchProductsTableData } from './useFetchProductsTableData'

import { getTableCopyFormat } from './helpers/tableCopyFormat'

import { getVariablesToBeAsked } from '../../AdditionalContractInfo/ContractFields/helpers'
import { bookingVariablesSelector } from '../../../../../../modules/selectors/mediaOrdersBookings'

import { updatingContractProductIdsInProgressSelector } from '../../../../../../modules/selectors/contracts'

import { MEDIA_PRODUCTS, VARIABLES } from '../../fields'

import useStyles from './styles'

export const QUOTATION_BUILDER_TABLE_ID = 'quotation-builder-table'

const ProductsTable = ({ formik, allowEdit, allowAutoSave, checkInventory = true, isAmendment }) => {
  const classes = useStyles()
  // when the Media order contract is in amendment process, we allow to edit the products
  const isAmendProcess = isAmendment && allowEdit

  const bookingVariables = useSelector(bookingVariablesSelector)
  const updatingProductIds = useSelector(updatingContractProductIdsInProgressSelector)

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [locationCategory, setLocationCategory] = useState(null)
  const [locationRegion, setLocationRegion] = useState(null)
  const { tableSort, setTableSort, editProductId, setEditProductId } = useContext(ContractBuilderContext)
  const { values, setFieldValue } = formik

  const products = values[MEDIA_PRODUCTS]
  const selectedProductsIds = useMemo(() => {
    return products.map(product => product.data?.id)
  }, [products])

  const {
    // contextSelfAccountData,
    currency
  } = useContext(MediaOrderFormContext)
  const currencySymbol = currency?.symbol

  const disableAddProduct = useDisableProductsActions({ products })
  const { handleSaveDataToBE } = useSaveData({ values, allowEdit, allowAutoSave })
  const { orderedProducts, handleProductsOrderUpdate, handleSortChange } = useOrderProducts({
    products,
    values,
    setFieldValue,
    setTableSort,
    allowEdit,
    allowAutoSave
  })

  const handleDeleteProduct = useCallback(
    productId => {
      const newProducts = products.filter(product => product.id !== productId)
      setFieldValue(MEDIA_PRODUCTS, newProducts)

      handleSaveDataToBE({ updatedProductValues: newProducts })
      // reset selected category - cover case when empty row was deleted
      setSelectedCategory(null)

      // Get the list of variables that should currently be visible based on booking details and new media products.
      const variablesToBeAsked = getVariablesToBeAsked(bookingVariables, newProducts)
      // If the number of variables to be shown is less than the current variables in form state...
      if (variablesToBeAsked.length < values[VARIABLES].length) {
        // Filter out the variables that are no longer applicable by matching their IDs.
        const newVariables = values[VARIABLES].filter(value => {
          return variablesToBeAsked.find(variable => variable.id === value.data.id)
        })
        // Update the form state to remove variables that are no longer needed.
        setFieldValue(VARIABLES, newVariables)
      }
    },
    [products, setFieldValue, handleSaveDataToBE, bookingVariables, values]
  )

  const handleEditProduct = useCallback(
    productId => {
      setEditProductId(productId)
    },
    [setEditProductId]
  )

  const assetColumn = useAssetColumn({
    handleSortChange,
    formik,
    editProductId,
    setEditProductId,
    selectedCategory,
    setSelectedCategory,
    locationCategory,
    setLocationCategory,
    locationRegion,
    setLocationRegion,
    setTableSort,
    selectedProductsIds
  })

  const { hasReachData } = useReachData({ selectedProducts: products, setFieldValue, isAmendment })
  useFetchProductsTableData({ allowFetch: allowEdit })

  const productPricesColumns = usePreviewProductColumns({
    classes,
    allowEdit,
    handleSortChange,
    hasReachData,
    locationRegion,
    setLocationRegion,
    locationCategory,
    setLocationCategory,
    assetColumn,
    selectedCategory,
    setSelectedCategory,
    formik,
    checkInventory,
    editProductId,
    isAmendment,
    updatingProductIds,
    isAmendProcess,
    currencySymbol,
    allowAutoSave,
    handleDeleteProduct,
    handleEditProduct
  })

  const tableCopyFormat = useMemo(() => {
    return getTableCopyFormat(hasReachData)
  }, [hasReachData])

  return (
    <Table
      className={classes.quotationsProductsTable}
      cols={productPricesColumns}
      data={orderedProducts}
      tableSort={tableSort}
      tableId={QUOTATION_BUILDER_TABLE_ID}
      footerClassName={classes.footer}
      onRowDrag={allowEdit ? handleProductsOrderUpdate : undefined}
      // enable copy functionality
      formatCopyCells={tableCopyFormat}
      Footer={
        <FooterSummary
          formik={formik}
          allowEdit={allowEdit}
          allowAutoSave={allowAutoSave}
          isAmendment={isAmendment}
          isAmendProcess={isAmendProcess}
          disableProductAdd={disableAddProduct}
          editProductId={editProductId}
          hasReachData={hasReachData}
          handleTotalEdit={handleEditProduct}
        />
      }
    />
  )
}

ProductsTable.propTypes = {
  formik: PropTypes.object,
  allowEdit: PropTypes.bool,
  allowAutoSave: PropTypes.bool,
  checkInventory: PropTypes.bool,
  isAmendment: PropTypes.bool
}

export default ProductsTable
